import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../../components/common/PageHeader";
import { useToast } from "../../../../components/toast-container";
import { getApiErrorMessage } from "../../../../lib/result-codes";
import { MerchantCouponListTable } from "../components/list/MerchantCouponListTable";
import { MerchantCouponStats } from "../components/list/MerchantCouponStats";
import { RestockMerchantCouponDialog } from "../components/list/RestockMerchantCouponDialog";
import { CreateCouponIcon } from "../components/list/icons/CreateCouponIcon";
import type { MerchantCouponListUIState } from "../data/merchantCouponList.data";
import {
  useDeleteMerchantCoupon,
  useGetMerchantCouponList,
  useGetMerchantRewardSummary,
  useRestockMerchantCoupon,
} from "../hooks";

const MerchantCouponListPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const {
    coupons,
    loading: isListLoading,
    error,
    filters,
    updateFilter,
    pagination,
    setPagination,
    refreshCoupons,
  } = useGetMerchantCouponList();
  const { summary, refreshSummary } = useGetMerchantRewardSummary();
  const { deleteMerchantCoupon, loading: isDeleting } = useDeleteMerchantCoupon();
  const { restockMerchantCoupon, loading: isRestocking } = useRestockMerchantCoupon();
  const [couponToRestock, setCouponToRestock] = useState<MerchantCouponListUIState | null>(null);

  const handlePageChange = (newPage: number) => {
    setPagination({ index: newPage });
  };

  const handleCreate = () => {
    navigate("/merchant/coupons/create");
  };

  const handleEdit = (coupon: MerchantCouponListUIState) => {
    navigate(`/merchant/coupons/edit/${coupon.id}`);
  };

  const handleDelete = async (coupon: MerchantCouponListUIState) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;

    try {
      await deleteMerchantCoupon(coupon.id);
      await refreshCoupons();
      await refreshSummary();
      addToast({ type: "success", title: "Success", message: "Coupon deleted successfully" });
    } catch (deleteError: unknown) {
      addToast({
        type: "error",
        title: "Error",
        message: getApiErrorMessage(deleteError, "Failed to delete coupon"),
      });
    }
  };

  const handleRestock = (coupon: MerchantCouponListUIState) => {
    setCouponToRestock(coupon);
  };

  const confirmRestock = async (quantity: number) => {
    if (!couponToRestock) return;

    try {
      await restockMerchantCoupon(couponToRestock.id, quantity);
      await refreshCoupons();
      await refreshSummary();
      addToast({ type: "success", title: "Success", message: "Coupon restocked successfully" });
      setCouponToRestock(null);
    } catch (restockError: unknown) {
      addToast({
        type: "error",
        title: "Error",
        message: getApiErrorMessage(restockError, "Failed to restock coupon"),
      });
    }
  };

  const handleCopy = (coupon: MerchantCouponListUIState) => {
    navigate(`/merchant/coupons/create?copyFrom=${coupon.id}`);
  };

  return (
    <div className="relative flex h-full w-full flex-col items-start bg-[rgba(241,245,249,0.5)] dark:bg-gray-900/50">
      <RestockMerchantCouponDialog
        coupon={couponToRestock}
        open={couponToRestock !== null}
        submitting={isRestocking}
        onOpenChange={(open) => {
          if (!open) setCouponToRestock(null);
        }}
        onConfirm={confirmRestock}
      />
      <div className="relative flex min-h-0 min-w-0 w-full flex-[1_0_0] flex-col items-start gap-4 bg-white p-4 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] sm:gap-6 sm:p-5 md:p-6 lg:gap-8 lg:p-8 xl:gap-10 xl:p-6 dark:bg-gray-800 dark:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.3)]">
        <div className="flex w-full flex-col gap-4 sm:gap-5 md:gap-6">
          <PageHeader
            title="Coupon List"
            className="mb-0 w-full"
            actions={(
              <button
                type="button"
                onClick={handleCreate}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-[8px] bg-[#00c29b] px-4 pb-[10.4px] pt-[9.6px] transition-colors hover:bg-[#00a888] sm:w-auto dark:bg-emerald-500 dark:hover:bg-emerald-600"
              >
                <CreateCouponIcon className="h-[16.25px] w-[16.25px] shrink-0 text-white" />
                <span className="text-center text-sm font-medium leading-5 text-[#f8fafc] whitespace-nowrap">
                  Create Coupon
                </span>
              </button>
            )}
          />
          <MerchantCouponStats summary={summary} />
        </div>

        <div className="relative flex w-full min-w-0 flex-col items-start">
          <MerchantCouponListTable
            coupons={coupons}
            loading={isListLoading || isDeleting || isRestocking}
            error={error}
            filters={filters}
            updateFilter={updateFilter}
            pagination={{
              page: pagination.index,
              totalPages: Math.max(1, Math.ceil(pagination.count / pagination.size)),
              onPageChange: handlePageChange,
            }}
            onRetry={() => void refreshCoupons()}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onRestock={handleRestock}
            onCopy={handleCopy}
          />
        </div>
      </div>
    </div>
  );
};

export default MerchantCouponListPage;
